/**
 * External dependencies
 */
import isEmpty from 'lodash/isEmpty';

/**
 * WP dependencies
 */
import { RichText, useBlockProps, useInnerBlocksProps } from '@wordpress/block-editor';
import { __ } from '@wordpress/i18n';

/**
 * General utilities
 */

import { removeHtmlTags } from '../utils/general-utils';

/**
 * The save function generates the frontend HTML output for the Featured Book block,
 * including the book cover image, title, contributors, description, and optional CTA.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#save
 *
 * @param {Object} props 
 * @param {Object} props.attributes 
 * @return {WPElement}
 */
const save = ({ attributes }) => {
  const {
    isbn,                 
    apiImage,            
    uploadedImage,       
    bookTitle,          
    contributors,        
    description, 
    format, 
    division,
    imprint,
    categories,        
    backgroundStyle,     
    backgroundColor,     
    titleColor,          
    contributorsColor,   
    descriptionColor,    
    linkColor,           
    showTitle,           
    showContributors,    
    showDescription,     
    showReadMore,        
    showCTA,             
    workId,              
    altTextManual, 
    uniqueId        
  } = attributes;

  // Image source determination logic
  /** @const {boolean} isEmptyImage - Check if uploaded image is empty/undefined */
  const isEmptyImage = isEmpty( uploadedImage );
  
  /** @const {string} imageSrc - Determine which image source to use (uploaded or API) */
  const imageSrc = !isEmptyImage ? uploadedImage.sizes.full.url : apiImage;

  // Early return if no image is available
  if ( !imageSrc ) {
    return null;
  }

  // WordPress block props configuration
  /** @const {Object} blockProps - Block wrapper props with CSS classes */
  const blockProps = useBlockProps.save({
    className: `block__featured-book ${backgroundStyle === 'full-width' ? 'is-full-width' : ''}`
  });

  // Inner blocks configuration for CTA section
  /** @const {Object} innerBlocksProps - Props for inner blocks (CTA button) */
  const { children, ...innerBlocksProps } = useInnerBlocksProps.save({
    className: 'featured-book__cta'
  });

  // Inline style objects for color customization
  /** @const {Object|undefined} containerStyle - Background color style object */
  const containerStyle = {
    backgroundColor: backgroundColor || undefined,
    '--featured-book-bg': backgroundColor || '#fff'
  };
  
  /** @const {Object|undefined} titleStyle - Title color style object */
  const titleStyle = titleColor ? { color: titleColor } : undefined;
  
  /** @const {Object|undefined} contributorsStyle - Contributors color style object */
  const contributorsStyle = contributorsColor ? { color: contributorsColor } : undefined;
  
  /** @const {Object|undefined} descriptionStyle - Description color style object */
  const descriptionStyle = descriptionColor ? { color: descriptionColor } : undefined;

  // Alt text determination with fallback logic
  /** @const {string} imageAltText - Alt text for the book cover image */
  const imageAltText = altTextManual || ( !isEmptyImage ? uploadedImage.alt : `${removeHtmlTags( bookTitle )} by ${removeHtmlTags( contributors )}` );

  return (
    <div {...blockProps} style={containerStyle}>
      <div className="featured-book--inner">
        <div className="featured-book__content-wrapper">
          {/* Book cover section with image and overlay */}
          <div className="featured-book__cover">
            <div className="image-wrapper">
              <div className="api-image-wrapper featured-book with-hover">
                <img 
                  className="api-image" 
                  src={imageSrc} 
                  alt={imageAltText} 
                  data-isbn={isbn} 
                  data-workid={workId} 
                  loading="lazy"
                />
              </div>
             
              <div className="overlay"></div>
              <button 
                className="featured-book__view-details"
                data-attributes={JSON.stringify({
                  title: removeHtmlTags( bookTitle ),
                  author: removeHtmlTags( contributors ),     
                  isbn,
                  apiImage,
                  uploadedImage,
                  description: removeHtmlTags( description ),
                  format, 
                  division,
                  imprint,
                  categories,     
                  workId,
                  altTextManual
                })}
              >
                {__( '+View Details', 'dwt-custom-theme' )}
              </button>
            </div>
          </div>

          {/* Text content section */}
          <div className="featured-book__text-content">
            {/* Conditional title rendering */}
            {showTitle && bookTitle.length > 0 && (
              <RichText.Content
                className="featured-book__title heading-3"
                tagName="h3"
                value={bookTitle}
                style={titleStyle}
              />
            )}

            {/* Conditional contributors rendering */}
            {showContributors && contributors.length > 0 && (
              <RichText.Content
                className="featured-book__contributors paragraph-1 text-medium"
                tagName="p"
                value={contributors}
                style={contributorsStyle}
              />
            )}

            {/* Conditional description rendering with read more functionality */}
            {showDescription && description.length > 0 && (
              <div className="featured-book__description-wrapper">
                <div 
                  className="featured-book__description paragraph-1 text-medium"
                  style={descriptionStyle}
                  data-link-color={linkColor}
                >
                  <RichText.Content 
                    className={`featured-book__description-content ${!showReadMore ? ' is-expanded' : ''}`}
                    tagName="div"
                    value={description} 
                    id={`featured-book__description-${uniqueId}`}
                    aria-live="polite"
                  />
                  {showReadMore && description.length > 480 && (
                    <button 
                      className="featured-book__read-more"
                      style={linkColor ? { color: linkColor } : {}}
                      aria-label={`Read More about ${removeHtmlTags( bookTitle )}`}
                      aria-expanded="false"
                      aria-controls={`featured-book__description-${uniqueId}`}
                    >
                      {__( 'Read More >', 'dwt-custom-theme' )}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Conditional CTA button rendering */}
          {showCTA && (
            <div className="featured-book__cta" {...innerBlocksProps}>
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default save;
